import { create } from 'zustand';
import { z } from 'zod';
import { regex } from 'utils/regex';

const courseSchema = z.object({
  courseName: z
    .string()
    .min(1, 'Course name is required')
    .max(255, 'Course name cannot exceed 255 characters'),
  courseCode: z.string().max(255, 'Course name cannot exceed 255 characters').optional(),
  courseCategory: z.string().min(1, 'Course category is required'),
  competencyCategory: z.string().min(1, 'Competency category is required'),
  description: z.string().optional(),
  categoryForCompetency: z.string().optional(),
  startDateTime: z.string().optional(),
  endDateTime: z.string().optional(),
  waReminder: z.string().optional(),
  courseType: z.string().optional(),
  targetAudience: z.string().max(50, 'Target audience cannot exceed 50 characters').optional(),
  programCategory: z.string().max(50, 'Program category cannot exceed 50 characters').optional(),
  specificJobBand: z.string().max(100, 'Specific job band cannot exceed 100 characters').optional(),
  tags: z.string().optional(),
  deliveryType: z.string().min(1, 'Delivery type is required'),
  learningObjectives: z.string().min(1, 'Learning objectives are required'),
  learningContent: z.string().optional(),
  groupTrainingType: z.string().min(1, 'Group training type is required'),
  trainingType: z.string().min(1, 'Training type is required'),
  levelOfTraining: z.string().optional(),
  learningFormat: z.string().optional(),
  fieldOrganizer: z.string().optional(),
  room: z.string().optional(),
  trainingLocation: z.string().optional(),
  modeOfDelivery: z.string().min(1, 'Mode of delivery is required'),
  learningHours: z
    .string()
    .min(1, 'Learning hours are required')
    .max(10, 'Learning hours cannot exceed 10 characters')
    .regex(/^\d+([.,]5)?$/, 'Learning hours must be a whole number or .5 (e.g., 3.5, 4.5)'),
  weightAttendance: z
    .string()
    .max(10, 'Weight attendance cannot exceed 10 characters')
    .refine(
      (value) => !value || regex.NUMBER_ONLY.test(value),
      'Weight attendance must contain only numbers'
    )
    .optional(),
  weightPreTest: z
    .string()
    .max(10, 'Weight pre-test cannot exceed 10 characters')
    .refine(
      (value) => !value || regex.NUMBER_ONLY.test(value),
      'Weight pre-test must contain only numbers'
    )
    .optional(),
  weightPostTest: z
    .string()
    .max(10, 'Weight post-test cannot exceed 10 characters')
    .refine(
      (value) => !value || regex.NUMBER_ONLY.test(value),
      'Weight post-test must contain only numbers'
    )
    .optional(),
  weightALP: z
    .string()
    .max(10, 'Weight ALP cannot exceed 10 characters')
    .refine(
      (value) => !value || regex.NUMBER_ONLY.test(value),
      'Weight ALP must contain only numbers'
    )
    .optional(),
  limIndicator: z.boolean().optional(),
  liaIndicator: z.boolean().optional(),
  passingFormula: z.string().min(1, 'Passing formula are required'),
  activities: z
    .array(
      z.object({
        activityName: z.string().min(1, 'Activity name is required'),
        activityType: z.string().min(1, 'Activity type is required'),
        activityId: z.string().min(1, 'Activity ID is required')
      })
    )
    .optional()
    .default([]),
  flagReviewSME: z.boolean().optional(),
  smeInternal: z.array(z.string()).optional().default([]),
  smeExternal: z.array(z.string()).optional().default([]),
  picApproval1: z.string().optional(),
  picApproval2: z.string().optional(),
  bcldModuleCode: z.string().optional(),
  creatorCode: z.string().min(1, 'Creator code are required'),
  tnaCourses: z.array(z.string()).optional().default([]),
  businessUnits: z.array(z.string()).optional().default([]),
  targetBinusian: z.string().optional(),
  jenisTraining: z.string().optional(),
  c: z.array(z.string()).optional().default([]),
  status: z.string().optional().default('draft'),
  // bcldLevel: z.string().min(1, 'BCLD level is required'),
  bcldLevel: z.string().optional(),
  bcldStreamOrFunda: z.string().min(1, 'Compulsory or Optional is required'),
  grpTraining: z.string().optional(),
  formulaID: z.string().optional()
});

export type CourseFormData = z.infer<typeof courseSchema>;

interface CourseState {
  formData: CourseFormData;
  detailCourse: any;
  currentStep: number;
  mode: 'add' | 'edit';
  setFormData: (_data: Partial<CourseFormData>) => void;
  setFormMode: (_data: 'add' | 'edit') => void;
  setDetailCourse: (_data: any) => void;
  setStep: (_data: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  reset: () => void;
}

export const useCourseStore = create<CourseState>((set) => ({
  formData: {
    courseName: '',
    categoryForCompetency: '',
    courseCode: '',
    courseCategory: '',
    courseType: '',
    competencyCategory: '',
    description: '',
    tags: '',
    deliveryType: '',
    learningObjectives: '',
    groupTrainingType: '',
    trainingType: '',
    learningHours: '',
    modeOfDelivery: '',
    limIndicator: false,
    liaIndicator: false,
    passingFormula: '',
    activities: [],
    flagReviewSME: false,
    smeInternal: [],
    smeExternal: [],
    picApproval1: '',
    picApproval2: '',
    creatorCode: '',
    tnaCourses: [],
    businessUnits: [],
    targetBinusian: '',
    status: 'draft',
    bcldLevel: '',
    bcldStreamOrFunda: '',
    grpTraining: '',
    c: []
  },
  detailCourse: null,
  mode: 'add',
  currentStep: 0,
  setFormData: (data) =>
    set((state) => ({
      formData: { ...state.formData, ...data }
    })),
  nextStep: () =>
    set((state) => ({
      currentStep: state.currentStep + 1
    })),
  prevStep: () =>
    set((state) => ({
      currentStep: Math.max(0, state.currentStep - 1)
    })),
  setFormMode: (data) => set(() => ({ mode: data })),
  setDetailCourse: (data) => set(() => ({ detailCourse: data })),
  setStep: (step) => set(() => ({ currentStep: step })),
  reset: () =>
    set({
      formData: {
        courseName: '',
        categoryForCompetency: '',
        courseCode: '',
        courseType: '',
        courseCategory: '',
        competencyCategory: '',
        description: '',
        tags: '',
        deliveryType: '',
        modeOfDelivery: '',
        learningObjectives: '',
        groupTrainingType: '',
        trainingType: '',
        learningHours: '',
        limIndicator: false,
        liaIndicator: false,
        passingFormula: '',
        activities: [],
        flagReviewSME: false,
        smeInternal: [],
        smeExternal: [],
        picApproval1: '',
        picApproval2: '',
        creatorCode: '',
        businessUnits: [],
        targetBinusian: '',
        status: 'draft',
        bcldLevel: '',
        bcldStreamOrFunda: '',
        grpTraining: '',
        tnaCourses: (() => {
          try {
            const raw = localStorage.getItem('tnaCoursesBuffer');
            if (!raw) return [];
            const arr = JSON.parse(raw);
            localStorage.removeItem('tnaCoursesBuffer');
            return Array.isArray(arr) ? arr.slice(0, 12) : [];
          } catch {
            return [];
          }
        })(),
        c: []
      },
      currentStep: 0
    })
}));

export { courseSchema };
